import { Layout } from "@/components/layout/Layout";
import { api, TimeEntry, ActivityCode } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format, parseISO, startOfYear, endOfYear } from "date-fns";
import { Loader2, Calendar, Clock, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";

interface PTOBalance {
  balance: number;
  accrued: number;
  used: number;
  pending: number;
}

export default function PTO() {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<PTOBalance | null>(null);
  const [ptoEntries, setPtoEntries] = useState<TimeEntry[]>([]);
  const [holidayEntries, setHolidayEntries] = useState<TimeEntry[]>([]);
  const [activityCodes, setActivityCodes] = useState<ActivityCode[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [balanceData, codesData] = await Promise.all([
          api.getPTOBalance(),
          api.getActivityCodes(),
        ]);
        
        setBalance(balanceData);
        setActivityCodes(codesData);

        const year = new Date().getFullYear();
        const entries = await api.getTimeEntries({
          startDate: format(startOfYear(new Date(year, 0, 1)), "yyyy-MM-dd"),
          endDate: format(endOfYear(new Date(year, 11, 31)), "yyyy-MM-dd"),
        });

        const ptoCodes = codesData
          .filter(c => c.category === "pto" && !c.name.toLowerCase().includes("holiday"))
          .map(c => c.code);
        const holidayCodes = codesData
          .filter(c => c.category === "holiday" || c.name.toLowerCase().includes("holiday"))
          .map(c => c.code);

        const allFiltered = entries.filter(e => ptoCodes.includes(e.activity_code) || holidayCodes.includes(e.activity_code));
        const sorted = allFiltered.sort((a, b) => b.entry_date.localeCompare(a.entry_date));
        setPtoEntries(sorted.filter(e => ptoCodes.includes(e.activity_code)));
        setHolidayEntries(sorted.filter(e => holidayCodes.includes(e.activity_code)));
      } catch (error) {
        console.error("Failed to load PTO data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight" data-testid="text-page-title">PTO & Time Off</h1>
          <p className="text-muted-foreground">Track your paid time off balance and history.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-primary text-primary-foreground border-none shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium opacity-80 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Available Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-heading" data-testid="text-pto-balance">
                {balance?.balance?.toFixed(1) || "0.0"} hrs
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Accrued YTD
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-heading" data-testid="text-pto-accrued">
                {balance?.accrued?.toFixed(1) || "0.0"} hrs
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Used YTD
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-heading text-amber-600" data-testid="text-pto-used">
                {balance?.used?.toFixed(1) || "0.0"} hrs
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pending
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-heading text-blue-600" data-testid="text-pto-pending">
                {balance?.pending?.toFixed(1) || "0.0"} hrs
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-t-4 border-t-primary shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              PTO History ({new Date().getFullYear()})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ptoEntries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No PTO entries recorded this year.
              </div>
            ) : (
              <div className="space-y-3">
                {ptoEntries.map(entry => {
                  const code = entry.activity_codes || activityCodes.find(c => c.code === entry.activity_code);
                  const hours = entry.minutes / 60;
                  
                  return (
                    <div 
                      key={entry.id} 
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border"
                      data-testid={`row-pto-entry-${entry.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-16 text-center">
                          <div className="text-xs text-muted-foreground uppercase">{format(parseISO(entry.entry_date), "MMM")}</div>
                          <div className="text-xl font-bold">{format(parseISO(entry.entry_date), "d")}</div>
                        </div>
                        <div>
                          <div className="font-medium">{code?.name || entry.activity_code}</div>
                          {entry.notes && (
                            <div className="text-sm text-muted-foreground">{entry.notes}</div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          entry.status === "approved" ? "bg-green-100 text-green-700" :
                          entry.status === "submitted" ? "bg-blue-100 text-blue-700" :
                          entry.status === "rejected" ? "bg-red-100 text-red-700" :
                          "bg-gray-100 text-gray-700"
                        }`}>
                          {entry.status}
                        </span>
                        <div className="font-mono font-bold text-right w-16">
                          {hours.toFixed(1)}h
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {holidayEntries.length > 0 && (
          <Card className="border-t-4 border-t-amber-500 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-500" />
                Holiday Hours ({new Date().getFullYear()})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {holidayEntries.map(entry => {
                  const code = entry.activity_codes || activityCodes.find(c => c.code === entry.activity_code);
                  const hours = entry.minutes / 60;
                  return (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-3 bg-amber-50/50 rounded-lg border border-amber-100"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-16 text-center">
                          <div className="text-xs text-muted-foreground uppercase">{format(parseISO(entry.entry_date), "MMM")}</div>
                          <div className="text-xl font-bold">{format(parseISO(entry.entry_date), "d")}</div>
                        </div>
                        <div>
                          <div className="font-medium">{code?.name || entry.activity_code}</div>
                          {entry.notes && <div className="text-sm text-muted-foreground">{entry.notes}</div>}
                        </div>
                      </div>
                      <div className="font-mono font-bold text-right w-16">{hours.toFixed(1)}h</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
